import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Fragment, useCallback, type ReactElement } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import {
  BreadcrumbSection,
  BreadcrumbDivider,
  Breadcrumb as SemanticBreadcrumb,
  Dropdown,
} from 'semantic-ui-react';
import { useIsClient } from 'usehooks-ts';

import CopyButton from '@components/copy-button';
import Link from '@components/link';
import type { SearchDropdownProps } from '@components/search-dropdown';
import { SearchDropdown } from '@components/search-dropdown';

import s from './breadcrumb.scss';

export type BreadcrumbItem = {
  /**
   * Unique key for this item. Not required, but recommended with multiple items
   * that could have the same text (e.g. '...' whilst loading). If not provided,
   * it will fall back to the item index.
   */
  key?: string;

  /**
   * The text to display for this breadcrumb item
   */
  text: string;

  /**
   * URL to link to when clicked
   */
  linkTo?: string;

  /**
   * Semantic UI Dropdown props. Renders a standard Dropdown for static options.
   * The `text` prop is inherited from the breadcrumb item.
   */
  dropdown?: Omit<DropdownProps, 'text'>;

  /**
   * SearchDropdown configuration for lazy-loaded options with search and
   * infinite scroll. The `text` prop is inherited from the breadcrumb item.
   */
  lazyLoadedDropdown?: Omit<SearchDropdownProps, 'text'>;

  /**
   * Callback function for rendering this item. If provided, it will completely override the default rendering.
   */
  render?: (item: BreadcrumbItem) => ReactElement;
};

export type BreadcrumbCopyLink = {
  ariaLabel: string;
  content: string;
  message?: string;
};

export type BreadcrumbProps = {
  /**
   * List of items to display in this breadcrumb
   */
  items: BreadcrumbItem[];

  /**
   * Callback function for rendering an item. If provided, it will completely override the default rendering.
   */
  renderItem?: (item: BreadcrumbItem) => ReactElement;

  /**
   * When set, renders a copy-to-clipboard control after the trail — e.g. a
   * shareable link to the current page.
   */
  copyLink?: BreadcrumbCopyLink;
};

/**
 * Builds a `copyLink` for the page's current URL once mounted on the client.
 * Stays `undefined` during SSR and the first client render so the hydrated
 * markup matches the server — otherwise the copy control's presence would
 * differ between the two and trip a hydration mismatch.
 */
export const useCurrentUrlCopyLink = (
  ariaLabel: string,
  message?: string,
): BreadcrumbCopyLink | undefined => {
  const isClient = useIsClient();

  return isClient
    ? { ariaLabel, content: window.location.href, message }
    : undefined;
};

/**
 * Multi-level breadcrumb control
 *
 * This control handles displaying a list of breadcrumb items that can be:
 *   - plain text
 *   - links
 *   - dropdown lists (static options or lazy-loaded with search)
 *   - custom components
 */
export const Breadcrumb = ({
  items,
  renderItem,
  copyLink,
}: BreadcrumbProps) => {
  useStyles(s);

  const renderBreadcrumbItem = useCallback(
    (item: BreadcrumbItem) => {
      const renderer = item.render ?? renderItem;
      if (renderer) {
        return renderer(item);
      }

      if (item.lazyLoadedDropdown) {
        return <SearchDropdown {...item.lazyLoadedDropdown} text={item.text} />;
      }

      if (item.dropdown) {
        const { className, ...rest } = item.dropdown;
        return (
          <Dropdown
            {...rest}
            className={classNames('darkreader-ignore', className)}
            text={item.text}
          />
        );
      }

      return item.linkTo ? (
        <Link to={item.linkTo} underline>
          {item.text}
        </Link>
      ) : (
        item.text
      );
    },
    [renderItem],
  );

  const trail = (
    <SemanticBreadcrumb as="nav" className={s.root}>
      {items.map((item, index) => (
        <Fragment key={item.key || `${item.text}-${index}`}>
          {index > 0 ? <BreadcrumbDivider className={s.divider} /> : null}
          <BreadcrumbSection
            className={classNames(
              s.section,
              item.dropdown || item.lazyLoadedDropdown ? s.dropdownList : '',
            )}
            active={index === items.length - 1}
          >
            {renderBreadcrumbItem(item)}
          </BreadcrumbSection>
        </Fragment>
      ))}
    </SemanticBreadcrumb>
  );

  if (!copyLink) {
    return trail;
  }

  return (
    <div className={s.withCopyLink}>
      {trail}
      <CopyButton
        icon
        inlineConfirmation
        iconName="linkify"
        content={copyLink.content}
        ariaLabel={copyLink.ariaLabel}
        tooltip={copyLink.ariaLabel}
        message={copyLink.message ?? 'Link copied to clipboard'}
      />
    </div>
  );
};
